import { ENVIRONMENTS, KNOWN_ENVIRONMENTS, SHELL } from "../constants/environment.js";

// Loads /runtime-config.json before any authenticated backend call is made.
// This is the single seam that lets the owner move servers, change domains,
// or swap reverse proxies without ever rebuilding the React static bundle:
// update runtime-config.json (or the reverse proxy/DNS in front of it),
// the browser refetches it on next load, and the API client picks up the
// new target automatically.

const REQUIRED_STRING_FIELDS = [
  "environment",
  "apiBaseUrl",
  "authBaseUrl",
  "appName",
  "version",
];

const RAW_IPV4_URL_PATTERN = /^https?:\/\/(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})(:\d+)?(\/|$)/i;

const URL_FIELDS = [
  "apiBaseUrl",
  "authBaseUrl",
  "trackingReadBaseUrl",
  "publicInviteBaseUrl",
];

export const RUNTIME_CONFIG_ERROR_REASONS = Object.freeze({
  NETWORK_ERROR: "network_error",
  INVALID_JSON: "invalid_json",
  MISSING_FIELDS: "missing_fields",
  UNSUPPORTED_ENVIRONMENT: "unsupported_environment",
  RAW_IP_DETECTED: "raw_ip_detected",
});

export class RuntimeConfigError extends Error {
  constructor(reason, detail) {
    super(`Runtime config failed to load: ${reason}`);
    this.name = "RuntimeConfigError";
    this.reason = reason;
    this.detail = detail;
  }
}

function isRawIpUrl(value) {
  return typeof value === "string" && RAW_IPV4_URL_PATTERN.test(value.trim());
}

// Raw IPv4 URLs are only tolerated for environments the owner uses to point
// the frontend at a temporary test backend (development/test). staging and
// production - and any future/unknown environment value - fail closed, since
// assertKnownEnvironment() already runs before this check.
const RAW_IP_ALLOWED_ENVIRONMENTS = new Set([ENVIRONMENTS.DEVELOPMENT, ENVIRONMENTS.TEST]);

function assertNoRawIpAddresses(config) {
  if (RAW_IP_ALLOWED_ENVIRONMENTS.has(config.environment)) return;

  const offendingFields = URL_FIELDS.filter((field) => isRawIpUrl(config[field]));
  if (offendingFields.length > 0) {
    throw new RuntimeConfigError(RUNTIME_CONFIG_ERROR_REASONS.RAW_IP_DETECTED, {
      offendingFields,
    });
  }
}

function assertRequiredFields(config) {
  const missing = REQUIRED_STRING_FIELDS.filter(
    (field) => typeof config?.[field] !== "string" || config[field].trim() === ""
  );
  if (missing.length > 0) {
    throw new RuntimeConfigError(RUNTIME_CONFIG_ERROR_REASONS.MISSING_FIELDS, {
      missing,
    });
  }
}

function assertKnownEnvironment(config) {
  if (!KNOWN_ENVIRONMENTS.includes(config.environment)) {
    throw new RuntimeConfigError(RUNTIME_CONFIG_ERROR_REASONS.UNSUPPORTED_ENVIRONMENT, {
      environment: config.environment,
    });
  }
}

/**
 * Normalizes and validates a raw parsed runtime-config.json payload.
 * Throws RuntimeConfigError (fail closed) on any violation.
 */
export function validateRuntimeConfig(rawConfig) {
  assertRequiredFields(rawConfig);
  assertKnownEnvironment(rawConfig);
  assertNoRawIpAddresses(rawConfig);

  return Object.freeze({
    environment: rawConfig.environment,
    apiBaseUrl: rawConfig.apiBaseUrl.trim(),
    authBaseUrl: rawConfig.authBaseUrl.trim(),
    trackingReadBaseUrl:
      typeof rawConfig.trackingReadBaseUrl === "string"
        ? rawConfig.trackingReadBaseUrl.trim()
        : "",
    publicInviteBaseUrl:
      typeof rawConfig.publicInviteBaseUrl === "string"
        ? rawConfig.publicInviteBaseUrl.trim()
        : "",
    // P141R17: optional, never required/hardcoded - a real Google Maps
    // JavaScript API key the owner configures out-of-band (billing/console
    // setup happens outside this codebase). Empty string (never a fake/
    // placeholder key) when unset - every real map surface treats that as
    // "no key configured" and falls back to its own honest keyless-embed or
    // blocked state, never a fabricated map.
    googleMapsApiKey:
      typeof rawConfig.googleMapsApiKey === "string" ? rawConfig.googleMapsApiKey.trim() : "",
    // Optional map background override, in the same spirit as the key above:
    // unset means the keyless default in components/basemapStyle.js, which is
    // what every deployment uses until somebody buys tiles. Set both together
    // - a tile provider's licence is what its attribution line states, so a
    // new tile URL with the old credit line would be a false claim.
    basemapTileUrl:
      typeof rawConfig.basemapTileUrl === "string" ? rawConfig.basemapTileUrl.trim() : "",
    basemapAttribution:
      typeof rawConfig.basemapAttribution === "string" ? rawConfig.basemapAttribution.trim() : "",
    appName: rawConfig.appName.trim(),
    supportContact:
      typeof rawConfig.supportContact === "string" ? rawConfig.supportContact.trim() : "",
    version: rawConfig.version.trim(),
  });
}

let cachedConfig = null;
let inFlightLoad = null;

async function fetchRuntimeConfig(configPath, fetchImpl) {
  let response;
  try {
    response = await fetchImpl(configPath, { cache: "no-store" });
  } catch (networkError) {
    throw new RuntimeConfigError(RUNTIME_CONFIG_ERROR_REASONS.NETWORK_ERROR, {
      message: networkError?.message,
    });
  }

  if (!response.ok) {
    throw new RuntimeConfigError(RUNTIME_CONFIG_ERROR_REASONS.NETWORK_ERROR, {
      status: response.status,
    });
  }

  let rawConfig;
  try {
    rawConfig = await response.json();
  } catch (parseError) {
    throw new RuntimeConfigError(RUNTIME_CONFIG_ERROR_REASONS.INVALID_JSON, {
      message: parseError?.message,
    });
  }

  return validateRuntimeConfig(rawConfig);
}

/**
 * Loads runtime config once and caches it for the session. Safe to call
 * from multiple places (API client, app shell) concurrently  -  only one
 * network request is made.
 */
export function loadRuntimeConfig({
  configPath = SHELL.RUNTIME_CONFIG_PATH,
  fetchImpl = typeof fetch === "function" ? fetch.bind(globalThis) : undefined,
} = {}) {
  if (cachedConfig) {
    return Promise.resolve(cachedConfig);
  }
  if (inFlightLoad) {
    return inFlightLoad;
  }
  if (typeof fetchImpl !== "function") {
    return Promise.reject(
      new RuntimeConfigError(RUNTIME_CONFIG_ERROR_REASONS.NETWORK_ERROR, {
        message: "No fetch implementation available",
      })
    );
  }

  inFlightLoad = fetchRuntimeConfig(configPath, fetchImpl)
    .then((config) => {
      cachedConfig = config;
      inFlightLoad = null;
      return config;
    })
    .catch((error) => {
      inFlightLoad = null;
      throw error;
    });

  return inFlightLoad;
}

/** Returns the cached config synchronously, or null if not yet loaded. */
export function getLoadedRuntimeConfig() {
  return cachedConfig;
}

/** Test-only reset so each test starts with a clean cache. */
export function resetRuntimeConfigForTests() {
  cachedConfig = null;
  inFlightLoad = null;
}
